import React, { useEffect, useState } from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import CircularProgress from '@mui/joy/CircularProgress';
import Alert from '@mui/joy/Alert';
import Divider from '@mui/joy/Divider';
import Sheet from '@mui/joy/Sheet';
import Link from '@mui/joy/Link';
import { Link as RouterLink } from 'react-router-dom';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { salesApi } from '../../api/sales.api';
import { Sale, SaleFulfillmentType, SaleShippingCarrier } from '../../types/sale';
import SaleStatusChip from '../SaleStatusChip';
import { Message } from '../../types/message';
import MessageBubble from './MessageBubble';
import { formatRecordReference } from '../../utils/formatRecordReference';
import { LAYOUT } from '../../theme/layoutTokens';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const CARRIER_LABELS: Record<SaleShippingCarrier, string> = {
  oca: 'OCA',
  andreani: 'Andreani',
  via_cargo: 'Vía Cargo',
  moto: 'Moto',
  unknown: 'Sin definir',
};

const FULFILLMENT_ICONS: Record<SaleFulfillmentType, React.ReactNode> = {
  shipping: <LocalShippingOutlinedIcon fontSize="small" />,
  pickup_branch: <StoreOutlinedIcon fontSize="small" />,
  undetermined: <HelpOutlineIcon fontSize="small" />,
};

function formatAddressSnapshot(addr: Sale['shippingAddress'] | Sale['pickupAddress']): string {
  if (!addr) return '';
  const parts = [addr.street, addr.number, addr.city, addr.state].filter(Boolean);
  return parts.join(', ');
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface OrderSummaryPanelProps {
  clientReference: string;
  messages: Message[];
}

// ─── Component ────────────────────────────────────────────────────────────────

const OrderSummaryPanel: React.FC<OrderSummaryPanelProps> = ({
  clientReference,
  messages,
}) => {
  const [sale, setSale] = useState<Sale | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!clientReference) return;
    setLoading(true);
    setError(false);
    setSale(null);

    salesApi
      .getByClientReference(clientReference)
      .then((sales) => {
        // Tomar la venta activa más reciente (no cancelada)
        const sorted = [...sales].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        );
        const active = sorted.find((s) => s.status !== 'cancelled') ?? sorted[0] ?? null;
        setSale(active);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [clientReference]);

  // Últimos 4 mensajes para la vista reducida
  const recentMessages = messages.slice(-4);

  // ── Loading ──
  if (loading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.level1',
        }}
      >
        <CircularProgress size="sm" />
      </Box>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <Box sx={{ p: 2, bgcolor: 'background.level1', flex: 1 }}>
        <Alert color="danger" size="sm">
          No se pudieron cargar los pedidos del cliente.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        bgcolor: 'background.level1',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {/* ══ Sin pedido activo ══════════════════════════════════════════════ */}
      {!sale ? (
        <Sheet
          variant="outlined"
          sx={{
            borderRadius: 'md',
            p: 3,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <ReceiptLongOutlinedIcon sx={{ fontSize: 36, color: 'neutral.400' }} />
          <Typography level="title-sm" sx={{ color: 'neutral.500' }}>
            Sin pedido activo
          </Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.400' }}>
            Este contacto no tiene una compra en curso.
          </Typography>
        </Sheet>
      ) : (
        <>
          {/* ══ Tarjeta de pedido ═══════════════════════════════════════════ */}
          <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'hidden' }}>
            {/* Header de la tarjeta */}
            <Box
              sx={{
                px: 2,
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.surface',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReceiptLongOutlinedIcon sx={{ fontSize: 15, color: 'neutral.500' }} />
                <Typography level="body-xs" sx={{ color: 'neutral.500', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                  Pedido{' '}
                  <Link
                    component={RouterLink}
                    to={`/sales/${sale.reference}`}
                    level="body-xs"
                    sx={{ fontWeight: 700 }}
                  >
                    #{formatRecordReference(sale.reference)}
                  </Link>
                </Typography>
              </Box>
              <SaleStatusChip status={sale.status} />
            </Box>

            {/* Productos */}
            <Box>
              {(sale.products ?? []).map((p, i) => (
                <Box
                  key={p.reference ?? i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-of-type': { borderBottom: 'none' },
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: 'neutral.softBg',
                      color: 'neutral.600',
                      borderRadius: 'xs',
                      fontSize: '11px',
                      fontWeight: 700,
                      px: 0.75,
                      py: 0.25,
                      flexShrink: 0,
                    }}
                  >
                    ×{p.quantity}
                  </Box>
                  <Typography level="body-sm" sx={{ flex: 1 }}>
                    {p.name}
                  </Typography>
                  <Typography level="body-sm" fontWeight="lg" sx={{ flexShrink: 0 }}>
                    {p.price > 0
                      ? formatCurrency(p.quantity * p.price)
                      : <Typography level="body-xs" color="success">Gratis</Typography>}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Total */}
            <Divider />
            <Box
              sx={{
                px: 2,
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: 'background.surface',
              }}
            >
              <Typography level="title-sm" fontWeight="lg">
                Total
              </Typography>
              <Typography
                level="title-md"
                fontWeight="xl"
                sx={{ color: 'primary.700' }}
              >
                {formatCurrency(sale.total)}
              </Typography>
            </Box>
          </Sheet>

          {/* ══ Tarjeta de envío / retiro ═══════════════════════════════════ */}
          <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'hidden' }}>
            {/* Header */}
            <Box
              sx={{
                px: 2,
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.surface',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {FULFILLMENT_ICONS[sale.fulfillmentType ?? 'undetermined']}
                <Typography
                  level="body-xs"
                  sx={{ color: 'neutral.500', textTransform: 'uppercase', letterSpacing: '.5px' }}
                >
                  {sale.fulfillmentType === 'shipping'
                    ? 'Envío'
                    : sale.fulfillmentType === 'pickup_branch'
                    ? 'Retiro en sucursal'
                    : 'Entrega sin definir'}
                </Typography>
              </Box>
            </Box>

            {/* Detalle de fulfillment */}
            <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {sale.fulfillmentType === 'shipping' && (
                <>
                  {sale.shippingCarrier && sale.shippingCarrier !== 'unknown' && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <LocalShippingOutlinedIcon sx={{ fontSize: 16, color: 'neutral.500', mt: 0.25, flexShrink: 0 }} />
                      <Box>
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                          Transportista
                        </Typography>
                        <Typography level="body-sm" fontWeight="md">
                          {CARRIER_LABELS[sale.shippingCarrier]}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {sale.shippingAddress && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <LocalShippingOutlinedIcon sx={{ fontSize: 16, color: 'neutral.500', mt: 0.25, flexShrink: 0, opacity: 0 }} />
                      <Box>
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                          Dirección de entrega
                        </Typography>
                        <Typography level="body-sm" fontWeight="md">
                          {formatAddressSnapshot(sale.shippingAddress)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </>
              )}

              {sale.fulfillmentType === 'pickup_branch' && (
                <>
                  {sale.branch && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <StoreOutlinedIcon sx={{ fontSize: 16, color: 'neutral.500', mt: 0.25, flexShrink: 0 }} />
                      <Box>
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                          Sucursal
                        </Typography>
                        <Typography level="body-sm" fontWeight="md">
                          {sale.branch.displayName}
                        </Typography>
                        {sale.branch.address && (
                          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                            {formatAddressSnapshot(sale.branch.address)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  {sale.pickupAddress && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <StoreOutlinedIcon sx={{ fontSize: 16, color: 'neutral.500', mt: 0.25, flexShrink: 0, opacity: 0 }} />
                      <Box>
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                          Dirección de retiro
                        </Typography>
                        <Typography level="body-sm" fontWeight="md">
                          {formatAddressSnapshot(sale.pickupAddress)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </>
              )}

              {(sale.fulfillmentType === 'undetermined' || !sale.fulfillmentType) && (
                <Typography level="body-sm" sx={{ color: 'neutral.400', fontStyle: 'italic' }}>
                  El método de entrega todavía no fue definido.
                </Typography>
              )}
            </Box>
          </Sheet>
        </>
      )}

      {/* ══ Conversación reciente ═══════════════════════════════════════════ */}
      {recentMessages.length > 0 && (
        <Box>
          <Typography
            level="body-xs"
            sx={{
              color: 'neutral.500',
              textTransform: 'uppercase',
              letterSpacing: '.5px',
              fontWeight: 700,
              mb: 1,
            }}
          >
            Conversación reciente
          </Typography>
          <Sheet
            variant="outlined"
            sx={{
              borderRadius: 'md',
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              bgcolor: 'background.surface',
            }}
          >
            {recentMessages.map((msg) => (
              <MessageBubble
                key={msg.messageId}
                message={msg}
                isOwn={Boolean(msg.fromMe)}
              />
            ))}
          </Sheet>
        </Box>
      )}
    </Box>
  );
};

export default OrderSummaryPanel;
